import {
  Input,
  Link,
  Button,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@nextui-org/react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconEye, IconEyeClosed } from "@tabler/icons-react";
import { useCountDown, useReactive } from "ahooks";
import { Transition } from "@headlessui/react";
import confetti from "canvas-confetti";
import { account } from "@pinnacle/api";

function passwordStrenth(v: string) {
  if (v.length < 6) {
    return "密码长度不能小于6位";
  }
  if (v.length > 20) {
    return "密码长度不能大于20位";
  }
  let level = 0;
  if (/[A-Z]/.test(v)) {
    level++;
  }
  if (/[a-z]/.test(v)) {
    level++;
  }
  if (/\d/.test(v)) {
    level++;
  }
  if (/[!@#$%^&*()_+]/.test(v)) {
    level++;
  }

  return level < 2 ? "密码强度不够" : true;
}

function validatePhone(v: string) {
  if (!/^\d{11}$/.test(v)) {
    return false;
  }
  return true;
}

export default function SignUp() {
  const navigate = useNavigate();
  const { search } = useLocation();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const registerForm = useReactive({
    phone: "",
    phoneCode: "",
    password: "",
    passwordRepeat: "",
    privacyAgree: false,
  });
  const [formValidateInfo, setFormValidInfo] = useState({
    phone: "",
    phoneCode: "",
    password: "",
    passwordRepeat: "",
  });

  const {
    isOpen: isPrivacyModalOpen,
    onOpen,
    onOpenChange: onPrivacyModalOpenChange,
  } = useDisclosure();
  const [registerStep, setRegisterStep] = useState<
    "phone pending" | "form pending" | "done"
  >("phone pending");
  const [phoneCodeSendStatus, setPhoneCodeSendStatus] = useState<
    "ready" | "sending" | "waiting"
  >("ready");
  const [phoneCodeAvaibleDate, setPhoneCodeAvaibleDate] = useState<number>(0);
  const [phoneCodeRetryCountdown] = useCountDown({
    targetDate: phoneCodeAvaibleDate,
    onEnd: () => {
      setPhoneCodeSendStatus("ready");
    },
  });
  const registerWarning = useReactive({
    message: "",
    title: "",
    show: false,
  });

  const sendPhoneCode = async (captcha: string) => {
    console.log(captcha);

    if (!validatePhone(registerForm.phone)) {
      setFormValidInfo((prv) => ({ ...prv, phone: "手机号格式不正确" }));
      return;
    }
    if (phoneCodeSendStatus !== "ready") {
      return;
    }
    setPhoneCodeSendStatus("sending");

    try {
      const res = await account.phoneCode({
        phone: registerForm.phone,
        captcha: captcha,
        service: "register",
      });
      if (res.data.sendStatus) {
        setPhoneCodeSendStatus("waiting");
        setPhoneCodeAvaibleDate(Date.now() + 1000 * 3 * 60); // 3分钟后重试
        setRegisterStep("form pending");
      } else if (res.data.captchaResult) {
        registerWarning.title = "短信验证码发送失败";
        registerWarning.message = res.data.message;
        registerWarning.show = true;
        setPhoneCodeSendStatus("ready");
      } else {
        // 人机验证失败
        setPhoneCodeSendStatus("ready");
      }
      return {
        captchaResult: res.data.captchaResult,
        bizResult: res.data.sendStatus,
      };
    } catch (e: any) {
      registerWarning.title = "短信验证码发送失败";
      registerWarning.message = e.message || e;
      registerWarning.show = true;
      setPhoneCodeSendStatus("ready");
      return {
        captchaResult: true,
        bizResult: false,
      };
    }
  };

  const register = async () => {
    setFormValidInfo({
      phone: "",
      phoneCode: "",
      password: "",
      passwordRepeat: "",
    });
    if (registerForm.password !== registerForm.passwordRepeat) {
      setFormValidInfo((prv) => ({ ...prv, passwordRepeat: "两次密码不一致" }));
      return;
    }
    const passwordValid = passwordStrenth(registerForm.password);
    if (passwordValid !== true) {
      setFormValidInfo((prv) => ({ ...prv, password: passwordValid }));
      return;
    }
    if (registerForm.phoneCode.length === 0) {
      setFormValidInfo((prv) => ({ ...prv, phoneCode: "请输入验证码" }));
      return;
    }
    if (!registerForm.privacyAgree) {
      onOpen();
      return;
    }
    // register
    try {
      await account.signUpByPhone({
        phone: registerForm.phone,
        password: registerForm.password,
        code: registerForm.phoneCode,
      });
    } catch (e: any) {
      registerWarning.title = "注册失败";
      registerWarning.message = e.message || e;
      registerWarning.show = true;
      return;
    }
    confetti({
      spread: 180,
      particleCount: 200,
      origin: { y: -0.02 },
    });
    setRegisterStep("done");
  };

  useEffect(() => {
    window.initAliyunCaptcha?.({
      SceneId: "o3zleadj", // 场景ID。根据步骤二新建验证场景后，您可以在验证码场景列表，获取该场景的场景ID
      prefix: "va013q", // 身份标。开通阿里云验证码2.0后，您可以在控制台概览页面的实例基本信息卡片区域，获取身份标
      mode: "popup", // 验证码模式。popup表示要集成的验证码模式为弹出式。无需修改
      element: "#captcha-phone-code", // 页面上预留的渲染验证码的元素，与原代码中预留的页面元素保持一致。
      button: "#captcha-phone-code-btn", // 触发验证码弹窗的元素。button表示单击登录按钮后，触发captchaVerifyCallback函数。您可以根据实际使用的元素修改element的值
      captchaVerifyCallback: sendPhoneCode, // 业务请求(带验证码校验)回调函数，无需修改
      onBizResultCallback: () => {}, // 业务请求结果回调函数，无需修改
      // getInstance: getCaptchaInstance, // 绑定验证码实例函数，无需修改
      slideStyle: {
        width: 360,
        height: 40,
      }, // 滑块验证码样式，支持自定义宽度和高度，单位为px。其中，width最小值为320 px
      language: "cn", // 验证码语言类型，支持简体中文（cn）、繁体中文（tw）、英文（en）
    });
    return () => {
      // 必须删除相关元素，否则再次mount多次调用 initAliyunCaptcha 会导致多次回调 captchaVerifyCallback
      document.getElementById("aliyunCaptcha-mask")?.remove();
      document.getElementById("aliyunCaptcha-window-popup")?.remove();
    };
  }, []);

  return (
    <div className="max-w-full w-[340px] ">
      <h1 className="text-2xl pb-10">👏注册加入我们</h1>
      <div className="flex flex-col gap-4 ">
        <div className="captcha-phone-code h-0" />
        {registerStep === "done" && (
          <div className="flex flex-col gap-4">
            <h1 className="text-2xl text-center">Pinnacle欢迎你的加入</h1>
            <p className="text-center">
              <Link
                onPress={() =>
                  navigate("/login/phone" + search, { replace: true })
                }
              >
                去登陆
              </Link>
            </p>
          </div>
        )}
        {registerStep !== "done" && (
          <Input
            isRequired
            label="手机号"
            variant="bordered"
            // labelPlacement="outside"
            startContent="+86"
            classNames={{
              input: ["text-md"],
            }}
            type="tel"
            name="phone"
            value={registerForm.phone}
            isInvalid={formValidateInfo.phone.length > 0}
            errorMessage={formValidateInfo.phone}
            onChange={(e) => {
              registerForm.phone = e.target.value;
              if (validatePhone(e.target.value)) {
                setFormValidInfo((prv) => ({ ...prv, phone: "" }));
              } else {
                setFormValidInfo((prv) => ({
                  ...prv,
                  phone: "手机号格式不正确",
                }));
              }
            }}
          />
        )}
        {registerStep === "phone pending" && (
          <p className="text-center text-small">
            已经有账号了?{" "}
            <Link
              size="sm"
              onPress={() =>
                navigate("/login/phone" + search, { replace: true })
              }
            >
              去登陆
            </Link>
          </p>
        )}
        <div
          className={
            registerStep === "form pending"
              ? "flex flex-row"
              : "flex gap-2 justify-end"
          }
        >
          {registerStep === "form pending" && (
            <Input
              isRequired
              label="手机验证码"
              variant="bordered"
              className="grow"
              name="phoneCode"
              isInvalid={formValidateInfo.phoneCode.length > 0}
              errorMessage={formValidateInfo.phoneCode}
              value={registerForm.phoneCode}
              onChange={(e) => {
                registerForm.phoneCode = e.target.value;
              }}
            />
          )}
          {registerStep !== "done" && (
            <Button
              fullWidth
              color="primary"
              disabled={phoneCodeSendStatus !== "ready"}
              className={registerStep === "form pending" ? "ml-2 h-auto" : ""}
              id="captcha-phone-code-btn"
            >
              {
                {
                  ready: "发送验证码",
                  sending: "发送中...",
                  waiting: `${Math.floor(
                    phoneCodeRetryCountdown / 1000,
                  )}秒后重试`,
                }[phoneCodeSendStatus]
              }
            </Button>
          )}
        </div>
        <Transition
          show={registerStep === "form pending"}
          enter="transition-opacity duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="gap-4 flex flex-col">
            <Input
              isRequired
              name="password"
              label="设置密码"
              type={passwordVisible ? "text" : "password"}
              variant="bordered"
              description="至少包含数字、大写字母、小写字母、特殊字符中的两种，长度在6-20字符之间"
              value={registerForm.password}
              isInvalid={formValidateInfo.password.length > 0}
              errorMessage={formValidateInfo.password}
              onChange={(e) => {
                registerForm.password = e.target.value;
                const passwordValid = passwordStrenth(e.target.value);
                if (passwordValid !== true) {
                  setFormValidInfo((prv) => ({
                    ...prv,
                    password: passwordValid,
                  }));
                } else {
                  setFormValidInfo((prv) => ({ ...prv, password: "" }));
                }
              }}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={() => {
                    setPasswordVisible((v) => !v);
                  }}
                >
                  {passwordVisible ? <IconEye /> : <IconEyeClosed />}
                </button>
              }
            />
            <Input
              isRequired
              name="password-repeat"
              label="重复密码"
              type={passwordVisible ? "text" : "password"}
              variant="bordered"
              value={registerForm.passwordRepeat}
              isInvalid={formValidateInfo.passwordRepeat.length > 0}
              errorMessage={formValidateInfo.passwordRepeat}
              onChange={(e) => {
                registerForm.passwordRepeat = e.target.value;
                if (e.target.value !== registerForm.password) {
                  setFormValidInfo((prv) => ({
                    ...prv,
                    passwordRepeat: "两次密码不一致",
                  }));
                } else {
                  setFormValidInfo((prv) => ({ ...prv, passwordRepeat: "" }));
                }
              }}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={() => {
                    setPasswordVisible((v) => !v);
                  }}
                >
                  {passwordVisible ? <IconEye /> : <IconEyeClosed />}
                </button>
              }
            />
            <div>
              <div className=" px-2 pb-2">
                <Checkbox
                  size="sm"
                  radius="full"
                  isSelected={registerForm.privacyAgree}
                  onValueChange={(c) => {
                    registerForm.privacyAgree = c;
                  }}
                >
                  我已阅读并同意《用户服务协议》和《隐私协议》
                </Checkbox>
              </div>

              <Button fullWidth color="primary" onPress={register}>
                即刻加入
              </Button>
            </div>
          </div>
        </Transition>
      </div>
      <Modal
        isOpen={isPrivacyModalOpen}
        placement="center"
        onOpenChange={onPrivacyModalOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                注册无法继续
              </ModalHeader>
              <ModalBody>
                <p>
                  是否阅读并同意《用户服务协议》和《隐私协议》？同意后即可继续注册。
                </p>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  取消
                </Button>
                <Button
                  color="primary"
                  onPress={() => {
                    registerForm.privacyAgree = true;
                    onClose();
                  }}
                >
                  是，明白
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={registerWarning.show}
        placement="center"
        onClose={() => {
          registerWarning.show = false;
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {registerWarning.title}
          </ModalHeader>
          <ModalBody>
            <p>{registerWarning.message}</p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onPress={() => {
                registerWarning.show = false;
              }}
            >
              好
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
